(() => {
  const posterSettings = { ...document.currentScript?.dataset };
  const mobile = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  document.querySelectorAll('[data-artbox-detail-videos]').forEach(root => {
    if (root.dataset.advInitialized) return;
    const rail = root.querySelector('.adv-rail');
    const cards = Array.from(root.querySelectorAll('.adv-card'));
    const videos = cards.map(card => card.querySelector('video'));
    if (!rail || !cards.length || videos.some(video => !video)) return;
    root.dataset.advInitialized = 'true';
    const pausedByUser = new WeakSet();
    const manualPlay = new WeakSet();
    const visible = new WeakMap();
    const pending = new WeakSet();
    const retryAfterPending = new WeakSet();
    const buttons = videos.map(video => video.parentElement.querySelector('.adv-play'));
    const dots = Array.from(root.querySelectorAll('[data-slide]'));
    const previous = root.querySelector('[data-adv-prev]');
    const next = root.querySelector('[data-adv-next]');
    const events = new AbortController();
    const on = (target, event, callback, options = {}) =>
      target?.addEventListener(event, callback, { ...options, signal: events.signal });
    let activeIndex = 0;
    let frame = 0;
    let movingTo = null;
    let movementTimeout = 0;
    let disposed = false;
    let pageActive = true;

    function load(video) {
      if (!video.getAttribute('src') && video.dataset.src) {
        video.src = video.dataset.src;
        video.preload = 'metadata';
        video.load();
      }
    }

    function eligible(video, index) {
      return !disposed && pageActive && root.isConnected && !document.hidden &&
        (!mobile.matches || index === activeIndex) &&
        (visible.get(video) || 0) >= 0.45 &&
        !pausedByUser.has(video) &&
        (!reducedMotion.matches || manualPlay.has(video));
    }

    function syncButton(video, index) {
      const button = buttons[index];
      if (!button) return;
      const playing = !video.paused;
      button.classList.toggle('is-playing', playing);
      button.setAttribute('aria-label', playing ? button.dataset.pauseLabel : button.dataset.playLabel);
    }

    function reconcilePlayback() {
      videos.forEach((video, index) => {
        if (!eligible(video, index)) video.pause();
      });
      videos.forEach((video, index) => {
        if (!eligible(video, index) || !video.paused) return;
        if (pending.has(video)) {
          retryAfterPending.add(video);
          return;
        }
        load(video);
        video.muted = true;
        pending.add(video);
        let failure = '';
        const request = video.play();
        Promise.resolve(request).then(() => {
          if (!eligible(video, index)) video.pause();
        }).catch(error => { failure = error.name; }).finally(() => {
          const retry = retryAfterPending.has(video);
          retryAfterPending.delete(video);
          pending.delete(video);
          syncButton(video, index);
          if (retry && failure !== 'NotAllowedError' && eligible(video, index) && video.paused) {
            scheduleUpdate();
          }
        });
      });
    }

    function nearestCard() {
      const rect = rail.getBoundingClientRect();
      const start = rect.left + (parseFloat(getComputedStyle(rail).paddingLeft) || 0);
      let nearest = 0;
      cards.forEach((card, index) => {
        if (Math.abs(card.getBoundingClientRect().left - start) <
          Math.abs(cards[nearest].getBoundingClientRect().left - start)) nearest = index;
      });
      return nearest;
    }

    function updateControls() {
      dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === activeIndex)));
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === cards.length - 1;
    }

    function update() {
      frame = 0;
      if (mobile.matches && movingTo === null) activeIndex = nearestCard();
      updateControls();
      reconcilePlayback();
    }

    function scheduleUpdate() {
      if (!frame) frame = requestAnimationFrame(update);
    }

    function finishMove() {
      movingTo = null;
      clearTimeout(movementTimeout);
      scheduleUpdate();
    }

    function go(index) {
      activeIndex = Math.max(0, Math.min(cards.length - 1, index));
      movingTo = activeIndex;
      updateControls();
      reconcilePlayback();
      const padding = parseFloat(getComputedStyle(rail).paddingLeft) || 0;
      const left = rail.scrollLeft + cards[activeIndex].getBoundingClientRect().left -
        rail.getBoundingClientRect().left - padding;
      rail.scrollTo({ left, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      clearTimeout(movementTimeout);
      movementTimeout = setTimeout(finishMove, 700);
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => visible.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
      scheduleUpdate();
    }, { threshold: [0, 0.2, 0.45, 0.65, 1] });

    videos.forEach((video, index) => {
      const key = video.dataset.posterKey;
      const poster = key && posterSettings[`poster${key.charAt(0).toUpperCase()}${key.slice(1)}`];
      if (!video.getAttribute('poster') && poster) video.poster = poster;
      video.removeAttribute('autoplay');
      video.removeAttribute('controls');
      video.muted = true;
      video.playsInline = true;
      observer.observe(video);
      on(video, 'play', () => {
        if (!eligible(video, index)) video.pause();
        else if (mobile.matches) videos.forEach(other => { if (other !== video) other.pause(); });
        syncButton(video, index);
      });
      on(video, 'pause', () => syncButton(video, index));
      on(buttons[index], 'click', () => {
        if (!video.paused) {
          pausedByUser.add(video);
          manualPlay.delete(video);
          video.pause();
        } else {
          pausedByUser.delete(video);
          manualPlay.add(video);
          if (mobile.matches) go(index);
          else reconcilePlayback();
        }
      });
    });
    on(previous, 'click', () => go(activeIndex - 1));
    on(next, 'click', () => go(activeIndex + 1));
    dots.forEach(dot => on(dot, 'click', () => go(Number(dot.dataset.slide))));
    on(rail, 'scroll', scheduleUpdate, { passive: true });
    on(rail, 'scrollend', finishMove);
    on(rail, 'pointerdown', finishMove, { passive: true });
    on(rail, 'touchstart', finishMove, { passive: true });
    on(rail, 'keydown', event => {
      if (!mobile.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      go(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    on(window, 'resize', scheduleUpdate, { passive: true });
    on(mobile, 'change', finishMove);
    on(reducedMotion, 'change', scheduleUpdate);
    on(document, 'visibilitychange', reconcilePlayback);
    on(window, 'pagehide', () => {
      pageActive = false;
      videos.forEach(video => video.pause());
    });
    on(window, 'pageshow', () => {
      pageActive = true;
      scheduleUpdate();
    });
    on(document, 'shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      disposed = true;
      videos.forEach(video => video.pause());
      observer.disconnect();
      events.abort();
      cancelAnimationFrame(frame);
      clearTimeout(movementTimeout);
      delete root.dataset.advInitialized;
    });
    scheduleUpdate();
  });
})();
